import * as React from 'react';

import { cn } from './cn';
import { Icon } from './Icon';
import { Text } from './Typography';

// Closed pagination primitive. Caller passes the controlled
// `currentPage` (1-indexed), the `totalPages` count, and an `onChange`
// handler; clicks on numbered buttons or prev/next fire
// `onChange(nextPage)`. SPA-friendly — no `<a href>` URL plumbing.
//
// `maxPagesShown` controls the maximum number of *numbered* slots
// rendered (excludes prev/next). When `totalPages` exceeds it, the
// row still renders exactly `maxPagesShown` slots — first + last are
// always present, and ellipsis fills any gaps. Constant slot count
// across navigation means the row's total width never changes.
//
// Slot width is computed from the digit count of `totalPages` so the
// widest numbered button (the largest page) always fits without
// growing. All slots share that width — numbered buttons and ellipsis
// alike — so the row stays visually rigid as the user navigates.
//
// Examples (maxPagesShown=7, totalPages=100):
//   current=1   → ‹ 1 2 3 4 5 … 100 ›   (window touches left)
//   current=5   → ‹ 1 … 4 5 6 … 100 ›   (centred)
//   current=50  → ‹ 1 … 49 50 51 … 100 ›
//   current=100 → ‹ 1 … 96 97 98 99 100 › (window touches right)

const ELLIPSIS = '…';

const computePageList = (current, total, maxShown) => {
    if (total <= maxShown) {
        return Array.from({ length: total }, (_, i) => i + 1);
    }

    // First + last are always present; the middle (`maxShown - 2`
    // slots) is filled with either contiguous pages or `ellipsis,
    // window, ellipsis` depending on where `current` sits.
    const middleCount = maxShown - 2;
    const leftThreshold = 1 + Math.ceil(middleCount / 2);
    const rightThreshold = total - Math.ceil(middleCount / 2);

    const slots = [1];

    if (current <= leftThreshold) {
        // Window touches left: 2, 3, …, (middleCount numbered) — no
        // left ellipsis, right ellipsis at the end of the middle.
        for (let i = 2; i < 2 + middleCount - 1; i += 1) {
            slots.push(i);
        }
        slots.push(ELLIPSIS);
    } else if (current >= rightThreshold) {
        // Window touches right.
        slots.push(ELLIPSIS);
        for (let i = total - middleCount + 1; i <= total - 1; i += 1) {
            slots.push(i);
        }
    } else {
        // Centred: ellipsis, current ± side, ellipsis.
        const innerCount = middleCount - 2;
        const side = Math.floor((innerCount - 1) / 2);
        slots.push(ELLIPSIS);
        for (let i = current - side; i <= current + (innerCount - 1 - side); i += 1) {
            slots.push(i);
        }
        slots.push(ELLIPSIS);
    }

    slots.push(total);
    return slots;
};

const computeSlotWidthPx = (totalPages) => {
    const digits = String(totalPages).length;
    return Math.max(36, digits * 10 + 6);
};

const BUTTON_BASE_CLASSNAME = 'tw-inline-flex tw-h-9 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-transition-colors focus:tw-outline-none focus-visible:tw-outline-none disabled:tw-cursor-not-allowed disabled:tw-opacity-50';
const NUMBERED_CLASSNAME = `${BUTTON_BASE_CLASSNAME} tw-px-0`;
const EDGE_CLASSNAME = `${BUTTON_BASE_CLASSNAME} tw-gap-2`;
const ACTIVE_CLASSNAME = 'tw-border-input tw-bg-background tw-text-foreground';
const INACTIVE_CLASSNAME = 'tw-border-transparent tw-bg-transparent tw-text-muted-foreground hover:tw-border-input hover:tw-bg-background hover:tw-text-foreground';

const Pagination = ({
    className: _className,
    style: _style,
    currentPage,
    maxPagesShown = 7,
    onChange,
    totalPages
}) => {
    if (totalPages <= 0) {
        return null;
    }

    const pages = computePageList(currentPage, totalPages, maxPagesShown);
    const canPrev = currentPage > 1;
    const canNext = currentPage < totalPages;
    const slotStyle = { width: `${computeSlotWidthPx(totalPages)}px` };

    return (
        <nav aria-label="pagination" className="tw-mx-auto tw-flex tw-w-full tw-justify-center">
            <ul className="tw-flex tw-flex-row tw-items-center tw-gap-1.5">
                <li>
                    <button
                        aria-label="Go to previous page"
                        className={cn(EDGE_CLASSNAME, INACTIVE_CLASSNAME, 'tw-pl-2.5 tw-pr-3.5')}
                        disabled={!canPrev}
                        onClick={() => onChange?.(currentPage - 1)}
                        type="button"
                    >
                        <Icon icon="chevronLeft" size="small" tone="inherit" />
                        <Text tone="inherit" variant="control">Previous</Text>
                    </button>
                </li>
                {pages.map((page, index) => (
                    <li key={`${page}-${index}`}>
                        {page === ELLIPSIS ? (
                            <span
                                aria-hidden
                                className="tw-flex tw-h-9 tw-items-center tw-justify-center tw-text-muted-foreground"
                                style={slotStyle}
                            >
                                <Icon icon="ellipsis" size="small" tone="inherit" />
                            </span>
                        ) : (
                            <button
                                aria-current={page === currentPage ? 'page' : undefined}
                                className={cn(NUMBERED_CLASSNAME, page === currentPage ? ACTIVE_CLASSNAME : INACTIVE_CLASSNAME)}
                                onClick={() => onChange?.(page)}
                                style={slotStyle}
                                type="button"
                            >
                                <Text tone="inherit" variant="control">{page}</Text>
                            </button>
                        )}
                    </li>
                ))}
                <li>
                    <button
                        aria-label="Go to next page"
                        className={cn(EDGE_CLASSNAME, INACTIVE_CLASSNAME, 'tw-pl-3.5 tw-pr-2.5')}
                        disabled={!canNext}
                        onClick={() => onChange?.(currentPage + 1)}
                        type="button"
                    >
                        <Text tone="inherit" variant="control">Next</Text>
                        <Icon icon="chevronRight" size="small" tone="inherit" />
                    </button>
                </li>
            </ul>
        </nav>
    );
};

export { Pagination };
